import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { cn } from "@/lib/format";

type Variant = "primary" | "quiet" | "outline" | "danger";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" }
>(function Button({ variant = "quiet", size = "md", className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        size === "md" ? "h-11 rounded-[var(--radius-control)] px-4 text-sm" : "h-9 rounded-[10px] px-3 text-sm",
        variant === "primary" &&
          "bg-film text-film-ink hover:bg-[#f7cf4f] active:bg-[#e0b21f] disabled:bg-raised disabled:text-mute disabled:opacity-100",
        variant === "quiet" && "text-paper hover:bg-raised active:bg-line",
        variant === "outline" && "border border-line text-paper hover:bg-raised",
        variant === "danger" && "text-danger hover:bg-danger/10",
        className,
      )}
      {...props}
    />
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }
>(function IconButton({ label, active, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex size-11 shrink-0 items-center justify-center rounded-full text-mute transition-colors hover:bg-raised hover:text-paper disabled:opacity-40",
        active && "bg-raised text-paper",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

/** Segmented choice used for kinds, aspect ratios and lengths. */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  className,
  size = "md",
}: {
  value: T;
  options: { value: T; label: ReactNode; disabled?: boolean; title?: string }[];
  onChange: (value: T) => void;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <div role="radiogroup" className={cn("inline-flex rounded-[var(--radius-control)] bg-desk p-1", className)}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={option.disabled}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={cn(
            "tabular inline-flex items-center justify-center gap-1.5 rounded-[9px] font-medium transition-colors disabled:opacity-35",
            size === "md" ? "h-9 min-w-11 px-3 text-sm" : "h-8 min-w-9 px-2.5 text-[13px]",
            option.value === value ? "bg-raised text-paper shadow-[inset_0_0_0_1px_var(--color-line)]" : "text-mute hover:text-paper",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A panel that is a bottom sheet on phones and a centred dialog on larger screens.
 * Radix handles focus trapping, Escape and scroll locking.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-in" />
        <Dialog.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] w-full max-w-full flex-col overflow-x-hidden rounded-t-[var(--radius-dock)] border-t border-line bg-panel pb-safe outline-none",
            "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:max-h-[80dvh] md:w-[min(92vw,560px)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[var(--radius-dock)] md:border md:pb-0",
            wide && "md:w-[min(94vw,960px)]",
          )}
        >
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line md:hidden" />
          <div className="flex items-start gap-3 px-5 pt-3 md:pt-5">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-lg font-semibold leading-tight">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-1 text-sm text-mute">{description}</Dialog.Description> : null}
            </div>
            <Dialog.Close asChild>
              <IconButton label="Close" className="-mr-2 -mt-1">
                <X className="size-5" />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="scrollbar-thin min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pb-5 pt-4 sm:px-5">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn("inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent", className)}
      aria-hidden
    />
  );
}
